import { useState, useEffect, useCallback } from 'react';
import { participantsApi } from '../../../../services/api/participants';
import type { Participant } from '../../../../types';

interface UseUnassignedParticipantsOptions {
  cohortId: string;
  refreshTrigger?: number;
}

export interface UseUnassignedParticipantsResult {
  participants: Participant[];
  loading: boolean;
  selectedParticipant: Participant | null;
  setSelectedParticipant: (participant: Participant | null) => void;
  removeParticipant: (userId: string) => void;
  refetch: () => Promise<void>;
}

/**
 * Fetches unassigned, active participants for a cohort and manages
 * selection state for time-slot filtering in the card view.
 */
export function useUnassignedParticipants({
  cohortId,
  refreshTrigger,
}: UseUnassignedParticipantsOptions): UseUnassignedParticipantsResult {
  const [participants, setParticipants] = useState<Participant[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedParticipant, setSelectedParticipant] = useState<Participant | null>(null);

  const fetchParticipants = useCallback(async () => {
    setLoading(true);
    try {
      const response = await participantsApi.listBasic(cohortId, {
        assignedStatus: 'unassigned',
        status: 'active',
        limit: 100,
      });
      setParticipants(response.data);
    } catch (error) {
      console.error('Failed to fetch unassigned participants:', error);
    } finally {
      setLoading(false);
    }
  }, [cohortId]);

  useEffect(() => {
    fetchParticipants();
  }, [fetchParticipants, refreshTrigger]);

  const removeParticipant = useCallback((userId: string) => {
    setParticipants((prev) => prev.filter((p) => p.user_id !== userId));
    setSelectedParticipant((prev) => (prev?.user_id === userId ? null : prev));
  }, []);

  return {
    participants,
    loading,
    selectedParticipant,
    setSelectedParticipant,
    removeParticipant,
    refetch: fetchParticipants,
  };
}
